/* V156 — Admin Ad Studio Tab.
   Central hub for all ad assets:
   - Quick links to public ad pages (/reklama/ai-agenti in 6 languages)
   - Brand assets count (cyborg portrait, voice sample)
   - Stats: which ads exist, where to record, where to share
   - Direct links to other ad tabs (Cinematic, FB Library, Visionary Copy)
*/
import { useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const AD_LANGUAGES = [
  { code: "sk", flag: "🇸🇰", name: "Slovenčina" },
  { code: "cs", flag: "🇨🇿", name: "Čeština" },
  { code: "en", flag: "🇬🇧", name: "English" },
  { code: "de", flag: "🇩🇪", name: "Deutsch" },
  { code: "pl", flag: "🇵🇱", name: "Polski" },
  { code: "hu", flag: "🇭🇺", name: "Magyar" },
];

const ASPECT_RATIOS = [
  { code: "9-16", label: "📱 9:16 Reels/TikTok", platforms: "IG Reels · TikTok · YouTube Shorts" },
  { code: "1-1", label: "🔲 1:1 FB Feed", platforms: "FB Feed · IG Post · LinkedIn" },
  { code: "16-9", label: "📺 16:9 YouTube", platforms: "YouTube · LinkedIn video" },
];

export default function AdminAdStudioTab() {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [adays, setAdays] = useState(30);

  useEffect(() => {
    const token = localStorage.getItem("admin_token") || "";
    if (!token) return;
    setLoading(true);
    axios
      .get(`${API}/studio/brand-assets`, { headers: { "X-Admin-Token": token } })
      .then((r) => setAssets(r.data.rows || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("admin_token") || "";
    if (!token) return;
    axios
      .get(`${API}/agents/ad-analytics?days=${adays}`, { headers: { "X-Admin-Token": token } })
      .then((r) => setAnalytics(r.data))
      .catch(() => setAnalytics(null));
  }, [adays]);

  const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  return (
    <div className="p-4 space-y-5" data-testid="ad-studio-tab">
      <h2 className="text-xl font-bold flex items-center gap-2">
        🎬 Ad Studio · Reklamy & Brand Assets
      </h2>
      <p className="text-sm text-zinc-400">
        Centrálny hub pre všetky reklamy. Klikni jazyk + aspect → otvor v novom tabe → nahraj cez záznamový button (MP4 sa stiahne).
      </p>

      {/* === AD ANALYTICS === */}
      <section className="border border-zinc-800 bg-gradient-to-br from-blue-950/30 to-zinc-950 p-4" data-testid="ad-analytics-block">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h3 className="text-lg font-bold flex items-center gap-2">
            📊 Ad-page štatistiky · /reklama/ai-agenti
          </h3>
          <select
            value={adays}
            onChange={(e) => setAdays(parseInt(e.target.value))}
            className="bg-zinc-900 border border-zinc-700 text-xs px-2 py-1 text-white"
            data-testid="ad-analytics-days"
          >
            <option value="7">7 dní</option>
            <option value="30">30 dní</option>
            <option value="90">90 dní</option>
          </select>
        </div>
        {!analytics && (
          <p className="text-xs text-zinc-500">Načítavam analytiku… (vyžaduje admin token v localStorage)</p>
        )}
        {analytics && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
              <MiniKpi label="Views" value={analytics.totals?.views || 0} testid="ad-kpi-views" />
              <MiniKpi label="Unikátni" value={analytics.totals?.unique_visitors || 0} testid="ad-kpi-uniq" />
              <MiniKpi label="Queue starts" value={analytics.totals?.queue_starts || 0} accent="text-amber-400" testid="ad-kpi-queue" />
              <MiniKpi label="Nahrávok" value={analytics.totals?.records_started || 0} accent="text-emerald-400" testid="ad-kpi-recs" />
              <MiniKpi label="Record rate" value={`${analytics.totals?.record_rate_pct || 0}%`} accent="text-blue-400" testid="ad-kpi-rate" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <BreakdownBox title="Podľa jazyka" rows={analytics.by_lang} keyName="lang" testid="ad-breakdown-lang" />
              <BreakdownBox title="Podľa aspect" rows={analytics.by_aspect} keyName="aspect" testid="ad-breakdown-aspect" />
              <BreakdownBox title="Top referrer (host)" rows={analytics.by_ref} keyName="ref_host" testid="ad-breakdown-ref" />
            </div>
          </>
        )}
      </section>

      {/* === BATCH RECORD MEGA-BUTTON === */}
      <section className="border-2 border-amber-500 bg-gradient-to-br from-amber-950/50 to-zinc-950 p-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-lg font-bold text-amber-400">
              🎬 Batch Record · 18 variantov naraz
            </h3>
            <p className="text-xs text-zinc-400 mt-1">
              6 jazykov × 3 aspect ratios = 18 MP4 súborov. ~14 min total. Browser
              sa spýta na permission iba <strong>raz</strong>.
            </p>
          </div>
          <a
            href={`${baseUrl}/reklama/ai-agenti?queue=1`}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-amber-500 hover:bg-amber-400 text-black font-black px-6 py-3 text-sm uppercase tracking-widest"
            data-testid="ad-batch-launcher"
          >
            ▶ Spustiť batch
          </a>
        </div>
        <p className="text-[11px] text-zinc-500 mt-3">
          Otvorí sa nový tab → klikni "START BATCH" → vyber tento tab v share dialógu
          → 18 × 44s = ~14 min → 18 .webm súborov v Downloads
        </p>
      </section>

      {/* === LIVE THUMBNAILS per language === */}
      <section className="border border-zinc-800 bg-zinc-950 p-4">
        <h3 className="text-lg font-bold mb-3">
          👁️ Live Preview · 6 jazykov
        </h3>
        <p className="text-xs text-zinc-400 mb-3">
          Každý thumbnail je živý iframe — vidíš presne ako sa reklama zobrazí. Klikni
          aby si otvoril v plnom okne.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {AD_LANGUAGES.map((lang) => (
            <div
              key={lang.code}
              className="bg-zinc-900 border border-zinc-800 hover:border-amber-500 transition-colors"
              data-testid={`ad-thumb-${lang.code}`}
            >
              <div className="relative w-full" style={{ paddingBottom: "177%" /* 9:16 */ }}>
                <iframe
                  src={`${baseUrl}/reklama/ai-agenti?lang=${lang.code}&hide_ui=1`}
                  title={`Ad preview ${lang.name}`}
                  className="absolute inset-0 w-full h-full pointer-events-none"
                  style={{ border: "0", transform: "scale(0.6)", transformOrigin: "top left", width: "166.67%", height: "166.67%" }}
                  loading="lazy"
                />
                <div className="absolute top-1 left-1 bg-black/80 text-white text-[10px] px-1.5 py-0.5 font-bold">
                  {lang.flag} {lang.code.toUpperCase()}
                </div>
              </div>
              <div className="p-2 flex gap-1">
                {ASPECT_RATIOS.map((ar) => (
                  <a
                    key={ar.code}
                    href={`${baseUrl}/reklama/ai-agenti?lang=${lang.code}&aspect=${ar.code}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 text-center text-[10px] bg-zinc-800 hover:bg-amber-500 hover:text-black px-1 py-1 transition-colors"
                  >
                    {ar.code}
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* === HERO: Animated Ad (V151-V154) === */}
      <section className="border border-amber-500/40 bg-gradient-to-br from-amber-950/30 to-zinc-950 p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
          <div>
            <h3 className="text-lg font-bold text-amber-400">
              ⚡ Animovaná Reklama · "AI Agenti pre e-shop"
            </h3>
            <p className="text-xs text-zinc-400 mt-1">
              44-sekundový loop · Variant B (loss-aversion €-driven) · Live AI prostredie (grafy, particles, HUD)
            </p>
          </div>
          <span className="text-[10px] tracking-widest font-black uppercase bg-emerald-900 text-emerald-300 px-2 py-1">
            LIVE V154
          </span>
        </div>

        {/* Storyboard */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-4 text-xs">
          <div className="bg-zinc-900/70 border border-zinc-800 p-2">
            <div className="text-red-400 font-bold mb-1">📦 OPUSTENÉ KOŠÍKY</div>
            <div className="text-zinc-300">Strácaš €3 200 mesačne. Bez agenta.</div>
          </div>
          <div className="bg-zinc-900/70 border border-zinc-800 p-2">
            <div className="text-amber-400 font-bold mb-1">📧 RUČNÝ INBOX</div>
            <div className="text-zinc-300">Míňaš €1 200 mesačne. Na rovnakých odpovediach.</div>
          </div>
          <div className="bg-zinc-900/70 border border-zinc-800 p-2">
            <div className="text-lime-400 font-bold mb-1">🎯 RIEŠENIE</div>
            <div className="text-zinc-300">Agent za €89/mes. Vráti €4 400 mesačne navyše.</div>
          </div>
        </div>

        {/* Language × Aspect matrix */}
        <div className="mb-3">
          <p className="text-[10px] tracking-widest uppercase text-amber-400 font-black mb-2">
            🌍 6 jazykov × 3 aspect ratios = 18 variantov
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {AD_LANGUAGES.map((lang) =>
              ASPECT_RATIOS.map((ar) => (
                <a
                  key={`${lang.code}-${ar.code}`}
                  href={`${baseUrl}/reklama/ai-agenti?lang=${lang.code}&aspect=${ar.code}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-zinc-900 border border-zinc-800 hover:border-amber-500 hover:bg-zinc-800 p-2 flex items-center justify-between text-xs text-white"
                  data-testid={`ad-link-${lang.code}-${ar.code}`}
                >
                  <span className="text-white font-medium">
                    {lang.flag} {lang.code.toUpperCase()} · {ar.code}
                  </span>
                  <span className="text-amber-400">↗</span>
                </a>
              ))
            )}
          </div>
        </div>

        {/* How to record */}
        <div className="bg-zinc-900 border border-zinc-700 p-3 text-xs">
          <p className="text-amber-400 font-bold mb-1">📥 Ako vyrobiť MP4:</p>
          <ol className="text-zinc-300 space-y-1 list-decimal list-inside">
            <li>Otvor odkaz nižšie (klikni vlajku + aspect)</li>
            <li>Vyber jazyk + aspect ratio v UI</li>
            <li>Klikni červené tlačidlo <strong className="text-red-400">🎥 NAHRAŤ MP4 (44s)</strong></li>
            <li>Browser sa spýta → vyber tento tab → 44s sa nahrá → .webm súbor sa stiahne automaticky</li>
            <li>Upload na IG Reels Ads / FB Ads / TikTok Ads / YouTube Shorts</li>
          </ol>
        </div>
      </section>

      {/* === BRAND ASSETS === */}
      <section className="border border-zinc-800 bg-zinc-950 p-4">
        <h3 className="text-lg font-bold mb-3">🎨 Brand Assets</h3>
        {loading && <p className="text-zinc-500 text-xs">Načítavam…</p>}
        {!loading && assets.length === 0 && (
          <p className="text-zinc-500 text-xs">
            Žiadne assety. Pridaj cyborg portrait / voice clip cez{" "}
            <code>POST /api/studio/brand-assets</code>
          </p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {assets.map((a) => (
            <div
              key={a.id}
              className="bg-zinc-900 border border-zinc-800 p-3 text-xs"
              data-testid={`brand-asset-${a.kind}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] tracking-widest uppercase font-black text-amber-400">
                  {a.kind}
                </span>
                <span className="text-[10px] text-zinc-500">
                  {a.language && `${a.language} · `}
                  {a.duration_s && `${a.duration_s}s`}
                </span>
              </div>
              <div className="font-bold text-white mb-1 line-clamp-2">{a.title}</div>
              {a.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {a.tags.slice(0, 4).map((t) => (
                    <span key={t} className="bg-zinc-800 text-zinc-400 px-1 py-0.5 text-[10px]">
                      {t}
                    </span>
                  ))}
                </div>
              )}
              <a
                href={a.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-amber-400 hover:text-amber-300 mt-2 inline-block text-[10px]"
              >
                Otvoriť asset ↗
              </a>
            </div>
          ))}
        </div>
      </section>

      {/* === RELATED ADMIN TABS === */}
      <section className="border border-zinc-800 bg-zinc-950 p-4">
        <h3 className="text-lg font-bold mb-2">📍 Súvisiace admin taby</h3>
        <p className="text-xs text-zinc-400 mb-3">
          Vedľa Ad Studia máš ďalšie tabs pre konkrétne typy reklám:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
          <TabInfo emoji="🎞️" label="Cinematic Ads" hint="AI generované creative (Gemini Nano Banana + GPT-Image)" />
          <TabInfo emoji="✨" label="Visionary Copy" hint="LLM-driven copy variants per audience" />
          <TabInfo emoji="📱" label="Reklamy (ads)" hint="A/B test live ads ktoré rotujú v UI" />
          <TabInfo emoji="📘" label="FB Ads Library" hint="Curated FB ad templates" />
          <TabInfo emoji="🎬" label="FB/IG Stratégia" hint="Audience targeting + creative pipeline" />
          <TabInfo emoji="🎯" label="VSL Funnely" hint="Video Sales Letter funnel analytika" />
          <TabInfo emoji="🔥" label="Social Proof" hint="Live activity ticker + customer count" />
          <TabInfo emoji="🌐" label="SEO Indexing" hint="Google Search Console submissions" />
        </div>
      </section>

      {/* === PUBLIC PAGES QUICK LINKS === */}
      <section className="border border-zinc-800 bg-zinc-950 p-4">
        <h3 className="text-lg font-bold mb-3">🚀 Verejné stránky / funnely</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
          <PublicLink href={`${baseUrl}/reklama/ai-agenti`} label="🎬 Animovaná reklama (V154)" />
          <PublicLink href={`${baseUrl}/agent-match`} label="🔍 Agent Match Scanner" />
          <PublicLink href={`${baseUrl}/prenajmi-si-agenta`} label="🎯 Rent-an-Agent Funnel" />
          <PublicLink href={`${baseUrl}/scan-agents`} label="🔬 Deep Site Scanner" />
          <PublicLink href={baseUrl} label="🏠 Homepage" />
          <PublicLink href="https://toprankpulse.com" label="🌐 Production (toprankpulse.com)" />
        </div>
      </section>
    </div>
  );
}

const TabInfo = ({ emoji, label, hint }) => (
  <div className="bg-zinc-900 border border-zinc-800 p-2 text-xs">
    <div className="font-bold mb-1">
      {emoji} {label}
    </div>
    <div className="text-zinc-500 text-[10px] leading-tight">{hint}</div>
  </div>
);

const PublicLink = ({ href, label }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="bg-zinc-900 border border-zinc-800 hover:border-amber-500 p-2 flex items-center justify-between"
  >
    <span>{label}</span>
    <span className="text-zinc-500">↗</span>
  </a>
);

const MiniKpi = ({ label, value, accent = "text-white", testid }) => (
  <div className="bg-zinc-900 border border-zinc-800 p-2" data-testid={testid}>
    <div className="text-[9px] tracking-widest uppercase font-black text-zinc-500">{label}</div>
    <div className={`text-lg font-black mt-0.5 ${accent}`}>{value}</div>
  </div>
);

const BreakdownBox = ({ title, rows = [], keyName, testid }) => {
  const max = Math.max(1, ...rows.map((r) => r.views || 0));
  return (
    <div className="bg-zinc-900 border border-zinc-800 p-2" data-testid={testid}>
      <div className="text-[10px] tracking-widest uppercase font-black text-zinc-500 mb-2">{title}</div>
      {rows.length === 0 && <div className="text-[10px] text-zinc-600">Žiadne dáta</div>}
      <div className="space-y-1">
        {rows.slice(0, 8).map((r) => (
          <div key={r[keyName]} className="flex items-center gap-2 text-[11px]">
            <span className="w-14 font-mono text-zinc-400 uppercase">{r[keyName] || "—"}</span>
            <div className="flex-1 bg-zinc-800 h-3 relative">
              <div className="absolute inset-y-0 left-0 bg-amber-500" style={{ width: `${(r.views / max) * 100}%` }} />
            </div>
            <span className="w-8 text-right text-white font-bold">{r.views}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
